/**
 * QSocials API types — mirror src/modules/<feature>/types.ts in qsocials-api.
 * Update in lockstep when the contract changes (no codegen yet).
 *
 * Endpoint envelope:
 *   ok    -> { success: true, ...payload }
 *   error -> { success: false, code: string, message: string }
 */

// ---------------- User profiles (Wave 2) ----------------

/**
 * Minimal public profile for a QSocials user, keyed by Logto `sub`.
 * Introduced in Wave 2: list endpoints now enrich each user entry with the
 * owner's `displayName` + `avatarUrl` (both nullable when the user has not
 * synced a profile yet). The Telegram Mini App syncs its own profile on
 * launch via `PUT /social/me/profile` (see useProfileSync).
 */
export interface UserProfileDTO {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
}

/**
 * Fields merged onto enriched list entries (friends / follows / requests).
 * Both may be `null` when the referenced user has no synced profile.
 */
export interface ProfileEnrichment {
  displayName?: string | null;
  avatarUrl?: string | null;
}

export interface UpsertMyProfileInput {
  displayName: string;
  avatarUrl?: string;
  /**
   * Digits-only Laravel `users.id`. Each user self-publishes their own
   * external id on launch so the participants list can resolve teammates
   * deterministically by Laravel id (see resolve-by-external-id below),
   * sidestepping the email-mismatch problem for Apple "Hide My Email" /
   * social-login users. Omit to leave the stored value untouched.
   */
  externalUserId?: string;
}

export interface UpsertMyProfileResponse {
  success: true;
  profile: UserProfileDTO;
}

export interface UserSearchResult {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
}

export interface UserSearchResponse {
  success: true;
  results: UserSearchResult[];
}

// ---------------- Resolve by email (identity bridge) ----------------

/**
 * Request body for `POST /social/users/resolve-by-email`.
 *
 * Used by the identity bridge: the TMA learns tournament participants only by
 * Laravel numeric id + email, never the Logto `sub` QSocials keys on. This
 * server-side resolver maps each email → Logto `sub` (via the Logto
 * Management API, cached in QSocials). Cap ≤ 100 emails per call.
 */
export interface ResolveUsersByEmailInput {
  emails: string[];
}

/**
 * One resolved user. `userId` is the Logto `sub`. Emails with no Logto match
 * are OMITTED from the results array (not returned with a null userId).
 */
export interface ResolveUsersByEmailResult {
  email: string;
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
}

export interface ResolveUsersByEmailResponse {
  success: true;
  results: ResolveUsersByEmailResult[];
}

// ---------------- Resolve by external id (identity bridge, deterministic) ----------------

/**
 * Request body for `POST /social/users/resolve-by-external-id`.
 *
 * The deterministic identity bridge: each user self-publishes their Laravel
 * `users.id` as `externalUserId` (see UpsertMyProfileInput). The TMA knows a
 * tournament participant's Laravel id directly from `profile.user.id`, so it
 * resolves the batch of ids → Logto `sub` here — no email round-trip, immune
 * to Apple "Hide My Email" / social-login email mismatches. Cap ≤ 100,
 * digits-only ids.
 */
export interface ResolveUsersByExternalIdInput {
  externalIds: string[];
}

/**
 * One resolved user. `userId` is the Logto `sub`; `externalUserId` echoes the
 * Laravel id that matched. Ids with no published profile are OMITTED from the
 * results array (not returned with a null userId).
 */
export interface ResolveUsersByExternalIdResult {
  externalUserId: string;
  userId: string;
  displayName?: string | null;
  avatarUrl?: string | null;
}

export interface ResolveUsersByExternalIdResponse {
  success: true;
  results: ResolveUsersByExternalIdResult[];
}

/**
 * Request body for `POST /social/users/profiles` — the reverse resolver:
 * a batch of Logto `subs` → their public profiles. Used to put human names +
 * avatars on lists that only know subs (e.g. QBank league standings). Cap ≤ 100.
 * (Endpoint to be added in QSocials — see docs/HANDOFF-qsocials-resolve-subs-to-profiles.md.)
 */
export interface ResolveProfilesBySubsInput {
  subs: string[];
}

/**
 * One resolved profile. Subs with no profile (never opened the TMA and no Logto
 * fallback) are OMITTED from `results`.
 */
export interface ResolveProfilesBySubsResult {
  sub: string;
  displayName?: string | null;
  avatarUrl?: string | null;
}

export interface ResolveProfilesBySubsResponse {
  success: true;
  results: ResolveProfilesBySubsResult[];
}

// ---------------- Friendships ----------------

export type FriendshipStatus = 'pending' | 'accepted' | 'blocked';

export interface FriendshipDTO extends ProfileEnrichment {
  friendUserId: string;
  status: FriendshipStatus;
  isIncoming?: boolean;
  isOutgoing?: boolean;
  /** ISO date string when the JSON is parsed from HTTP. */
  since: string;
}

export interface FriendsListResponse {
  success: true;
  count: number;
  friends: FriendshipDTO[];
}

export interface FriendRequestsResponse {
  success: true;
  incoming: FriendshipDTO[];
  outgoing: FriendshipDTO[];
}

// ---------------- Follows ----------------

export interface FollowDTO extends ProfileEnrichment {
  userId: string;
  since: string;
}

export interface FollowingListResponse {
  success: true;
  total: number;
  count: number;
  following: FollowDTO[];
}

export interface FollowersListResponse {
  success: true;
  total: number;
  count: number;
  followers: FollowDTO[];
}

export interface FollowCheckResponse {
  success: true;
  iFollow: boolean;
  followsMe: boolean;
}

// ---------------- Presence ----------------

export type PresenceStatus =
  | 'online'
  | 'idle'
  | 'in_tournament'
  | 'watching'
  | 'offline';
export type PresenceSurface = 'telegram' | 'web' | 'mobile';
export type ContextRefType = 'tournament' | 'match';

export interface ContextRef {
  type: ContextRefType;
  id: string;
}

export interface PresenceDTO {
  userId: string;
  status: PresenceStatus;
  contextRef?: ContextRef;
  surfaces: PresenceSurface[];
  lastSeenAt: string;
}

export interface HeartbeatInput {
  status?: PresenceStatus;
  surface?: PresenceSurface;
  context?: ContextRef | null;
}

export interface PresenceResponse {
  success: true;
  presence: PresenceDTO;
}

export interface OnlineFeedItem extends ProfileEnrichment {
  userId: string;
  status: PresenceStatus;
  contextRef?: ContextRef;
  surfaces: PresenceSurface[];
  lastSeenAt: string;
  relation: 'friend' | 'following' | 'both';
}

export interface OnlineFeedResponse {
  success: true;
  count: number;
  feed: OnlineFeedItem[];
}

// ---------------- Public profile (view another user) ----------------

/**
 * Relationship between two users from the *caller's* perspective. A pair is a
 * single QSocials friendships doc, so the friend states are mutually exclusive.
 * Mirrors qsocials-api src/modules/friendships/types.ts `FriendRelation`.
 */
export type FriendRelation =
  | 'none'
  | 'pending_incoming' // the other user sent ME a request
  | 'pending_outgoing' // I sent the other user a request
  | 'accepted'
  | 'blocked_by_me'
  | 'blocked_by_them';

export interface ProfileRelationship {
  friendStatus: FriendRelation;
  iFollow: boolean; // I follow them
  followsMe: boolean; // they follow me
  canMessage: boolean; // === accepted — the DM gate
  mutualFriends: number;
}

export interface ProfilePresenceLite {
  status: PresenceStatus;
  /** ISO date string when parsed from HTTP. */
  lastSeenAt: string;
}

/**
 * Aggregated public profile of a user (by Logto `sub`) returned by
 * `GET /social/users/:userId`. Identity + counts are visible to any
 * authenticated caller; `presence` is non-null only for accepted friends (or
 * self). Mirrors qsocials-api src/modules/profile/types.ts `PublicProfileDTO`.
 */
export interface PublicProfileDTO {
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
  externalUserId: string | null;
  isSelf: boolean;
  relationship: ProfileRelationship;
  counts: { friends: number; followers: number; following: number };
  presence: ProfilePresenceLite | null;
}

export interface PublicProfileResponse {
  success: true;
  profile: PublicProfileDTO;
}

// ---------------- Watch (co-watching) ----------------

export interface WatchTournamentDTO {
  tournamentId: string;
  watcherCount: number;
  /** Capped to 50 server-side. */
  watchers: string[];
}

export interface WatchResponse {
  success: true;
  watch: WatchTournamentDTO;
  iAmWatching?: boolean;
}

// ---------------- Cheers ----------------

export const ALLOWED_CHEER_EMOJIS = ['🔥', '💪', '👏', '😍', '💯'] as const;
export type CheerEmoji = (typeof ALLOWED_CHEER_EMOJIS)[number];

export interface CheerDTO {
  matchId: string;
  userId: string;
  emoji: string;
  ts: string;
}

export interface CheerAllowedResponse {
  success: true;
  allowed: readonly string[];
}

export interface CheerAggregateResponse {
  success: true;
  matchId: string;
  total: number;
  byEmoji: Record<string, number>;
  lastN: CheerDTO[];
  myCount: number;
}

export interface CheerPostResponse {
  success: true;
  cheer: CheerDTO;
}

// ---------------- Error envelope ----------------

export interface SocialErrorBody {
  success: false;
  code: string;
  message: string;
}
